import { useTranslation } from 'react-i18next';

import {
    Body1,
    Box,
    Flex,
    Heading5,
    HorizontalDivider,
    Stack,
    Stepper,
    type StepperStep,
    Subtitle2,
} from '@myphx-engineering/phx-design-system';
import type { SemanticColorVariants } from '@myphx-engineering/phx-design-system/framework';

import type {
    MedicareBridgeInfoPanel,
    PathwayOptionConfig,
} from '../../../../configuration/shared';
import type { FillPathway } from '../../../../contexts/fill';

import { MedicareBridgeEligibilityCard } from './MedicareBridgeEligibilityCard';

const CARD_BULLET_COLOR: Partial<Record<FillPathway, SemanticColorVariants>> = {
    government: 'success',
    'self-pay': 'info',
};

interface MedicareBridgeEligibilityStepperProps {
    options: PathwayOptionConfig[];
    infoPanel?: MedicareBridgeInfoPanel;
}

export const MedicareBridgeEligibilityStepper = ({
    options,
    infoPanel,
}: MedicareBridgeEligibilityStepperProps) => {
    const { t } = useTranslation();

    const infoPanelStep: StepperStep[] = infoPanel
        ? [
              {
                  iconProps: { name: infoPanel.icon, color: 'gray' },
                  label: (
                      <Subtitle2 data-testid="pharmacy-select-eligibility-verification-heading">
                          {infoPanel.heading}
                      </Subtitle2>
                  ),
                  description: (
                      <Stack
                          gap={0}
                          data-testid="pharmacy-select-eligibility-verification-description"
                      >
                          <Body1>{infoPanel.description}</Body1>
                          {infoPanel.outcomesLabel && (
                              <Box pt="xs">
                                  <Subtitle2>
                                      {infoPanel.outcomesLabel}
                                  </Subtitle2>
                              </Box>
                          )}
                      </Stack>
                  ),
                  allowStepClick: false,
                  branching: true,
              },
          ]
        : [];

    const optionSteps: StepperStep[] = options.map((option, index) => {
        const color: SemanticColorVariants =
            CARD_BULLET_COLOR[option.fillPathway] ?? 'gray';
        const isLast = index === options.length - 1;

        return {
            ...(option.content.headerIcon && {
                iconProps: { name: option.content.headerIcon, color },
            }),
            allowStepClick: false,
            description: (
                <Stack gap="xs">
                    <MedicareBridgeEligibilityCard option={option} />
                    {!isLast && (
                        <Flex align="center" gap="sm">
                            <Box style={{ flex: 1 }}>
                                <HorizontalDivider />
                            </Box>
                            <Heading5>{t('common.or')}</Heading5>
                            <Box style={{ flex: 1 }}>
                                <HorizontalDivider />
                            </Box>
                        </Flex>
                    )}
                </Stack>
            ),
        };
    });

    return (
        <Stepper
            active={-1}
            orientation="vertical"
            steps={[...infoPanelStep, ...optionSteps]}
            data-testid="pharmacy-select-two-outcome-stepper"
        />
    );
};
